/**
 * A value the parent reports on ITS schedule, held for whoever mounts later.
 *
 * The dashboard's phone and dialer frames post their state when something
 * changes, not when the module happens to be listening. A subscriber that
 * mounts between two reports — the Communications module after a route
 * change, a Call button after a tab switch — would otherwise sit on the empty
 * default until the next call happened to move something. So the last value is
 * kept, handed to every new subscriber at once, and fanned out on each report.
 */
export interface HostLatch<T> {
  /** Current value now, and on every change. Returns the unsubscribe. */
  subscribe: (handler: (value: T) => void) => () => void;
  /** A new report from the parent. */
  publish: (value: T) => void;
  /** Drop every subscriber — the bridge is going away. */
  clear: () => void;
}

export function makeHostLatch<T>(initial: T): HostLatch<T> {
  const listeners = new Set<(value: T) => void>();
  let current = initial;
  return {
    subscribe: (handler) => {
      listeners.add(handler);
      handler(current);
      return () => {
        listeners.delete(handler);
      };
    },
    publish: (value) => {
      current = value;
      for (const listener of listeners) listener(value);
    },
    clear: () => listeners.clear(),
  };
}
